/**
 * Custom useOAuth Hook
 * Provides OAuth authentication state management with PKCE flow
 * TASK-086: Updated to use httpClient for gateway enforcement
 */

import { useState, useEffect, useCallback } from 'react';
import { mapOAuthError, OAuthError } from '../utils/oauthErrorHandler';

export interface OAuthUser {
  id: string;
  email: string;
  display_name: string;
  provider: string;
}

export interface OAuthStatus {
  connected: boolean;
  provider?: string;
  user_info?: {
    email: string;
    display_name: string;
    connected_at: string;
  };
  last_sync?: string;
}

export interface UseOAuthReturn {
  // State
  isAuthenticated: boolean;
  user: OAuthUser | null;
  status: OAuthStatus | null;
  isLoading: boolean;
  error: string | null;
  errorDetails: OAuthError | null;

  // Actions
  connect: (scopes?: string[]) => Promise<void>;
  disconnect: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  clearError: () => void;

  // PKCE Flow helpers
  isConnecting: boolean;
}

const DEFAULT_SCOPES = [
  'https://graph.microsoft.com/Mail.Read',
  'https://graph.microsoft.com/Calendars.Read',
  'https://graph.microsoft.com/User.Read'
];

export const useOAuth = (): UseOAuthReturn => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<OAuthUser | null>(null);
  const [status, setStatus] = useState<OAuthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<OAuthError | null>(null);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
    setErrorDetails(null);
  }, []);

  // Fetch OAuth status from backend
  // TASK-086: Uses httpClient for gateway enforcement
  const refreshStatus = useCallback(async () => {
    try {
      setError(null);
      // TASK-548(c): the Microsoft OAuth token lives under its own key (ms_oauth_token), separate
      // from the app session token (auth_token). This hook's isAuthenticated/user is LOCAL state
      // meaning "Microsoft connected", not the app login.
      const oauthToken = localStorage.getItem('ms_oauth_token');

      if (!oauthToken) {
        setStatus({ connected: false });
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
        return;
      }

      // Raw fetch carrying the OAuth token — NOT httpClient, which would inject auth_token and whose
      // 401 interceptor would wipe the advisor's app session on an expired OAuth token.
      const statusResponse = await fetch('/auth/oauth/status', {
        headers: { 'Authorization': `Bearer ${oauthToken}` }
      });

      if (statusResponse.status === 401) {
        localStorage.removeItem('ms_oauth_token');
        setStatus({ connected: false });
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
        return;
      }
      if (!statusResponse.ok) {
        throw new Error(`Failed to fetch OAuth status: ${statusResponse.statusText}`);
      }
      const statusData: OAuthStatus = await statusResponse.json();

      setStatus(statusData);
      setIsAuthenticated(statusData.connected);

      if (statusData.connected && statusData.user_info) {
        setUser({
          id: '', // Will be populated from token data
          email: statusData.user_info.email,
          display_name: statusData.user_info.display_name,
          provider: statusData.provider || 'microsoft'
        });
      } else {
        setUser(null);
      }

    } catch (error: any) {
      // Handle 401 (token expired/invalid) — clear only the OAuth token, never the app session.
      if (error?.status === 401) {
        localStorage.removeItem('ms_oauth_token');
        setStatus({ connected: false });
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
        return;
      }

      const oauthError = mapOAuthError(error);
      setError(oauthError.message);
      setErrorDetails(oauthError);
      setStatus({ connected: false });
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Connect to OAuth provider
  const connect = useCallback(async (scopes: string[] = DEFAULT_SCOPES) => {
    setIsConnecting(true);
    setError(null);

    try {
      // Generate redirect URI
      const redirectUri = `${window.location.origin}/auth/oauth/microsoft/callback`;
      
      // Request OAuth authorization URL
      const response = await fetch('/api/auth/oauth/microsoft/authorize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider: 'microsoft',
          scopes,
          redirect_uri: redirectUri
        })
      });

      if (!response.ok) {
        throw new Error(`Authorization request failed: ${response.statusText}`);
      }

      const { auth_url, state, code_verifier } = await response.json();

      // Store PKCE parameters
      sessionStorage.setItem('oauth_state', state);
      sessionStorage.setItem('oauth_code_verifier', code_verifier);
      sessionStorage.setItem('oauth_redirect_uri', redirectUri);

      // Open OAuth popup
      const popup = window.open(
        auth_url,
        'oauth-popup',
        'width=500,height=700,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        throw new Error('Failed to open OAuth popup. Please allow popups for this site.');
      }

      // Return promise that resolves when OAuth flow completes
      return new Promise<void>((resolve, reject) => {
        const handleMessage = async (event: MessageEvent) => {
          if (event.origin !== window.location.origin) {
            return;
          }

          if (event.data.type === 'OAUTH_RESPONSE') {
            window.removeEventListener('message', handleMessage);
            
            if (event.data.error) {
              reject(new Error(event.data.error));
              return;
            }

            if (event.data.code && event.data.state) {
              try {
                // Exchange code for tokens
                const tokenResponse = await fetch('/api/auth/oauth/microsoft/token', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    code: event.data.code,
                    code_verifier: sessionStorage.getItem('oauth_code_verifier'),
                    redirect_uri: redirectUri,
                    state: event.data.state
                  })
                });

                if (!tokenResponse.ok) {
                  const errorData = await tokenResponse.json();
                  throw new Error(errorData.detail || 'Token exchange failed');
                }

                const tokenData = await tokenResponse.json();

                // TASK-548(c): store under the OAuth-specific key, NOT auth_token — this token has
                // no account_id and would 401 every app request if it became the session token.
                if (tokenData.access_token) {
                  localStorage.setItem('ms_oauth_token', tokenData.access_token);
                }

                // Update user state
                if (tokenData.user_info) {
                  setUser({
                    id: tokenData.user_info.id,
                    email: tokenData.user_info.email,
                    display_name: tokenData.user_info.display_name,
                    provider: tokenData.user_info.provider
                  });
                  setIsAuthenticated(true);
                }

                // Clean up session storage
                sessionStorage.removeItem('oauth_state');
                sessionStorage.removeItem('oauth_code_verifier');
                sessionStorage.removeItem('oauth_redirect_uri');

                // Refresh status
                await refreshStatus();
                resolve();

              } catch (error) {
                reject(error);
              }
            }
          }
        };

        window.addEventListener('message', handleMessage);

        // Handle popup closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', handleMessage);
            
            if (sessionStorage.getItem('oauth_state')) {
              reject(new Error('OAuth flow was cancelled'));
            }
          }
        }, 1000);
      });

    } catch (error) {
      const oauthError = mapOAuthError(error);
      setError(oauthError.message);
      setErrorDetails(oauthError);
      throw new Error(oauthError.message);
    } finally {
      setIsConnecting(false);
    }
  }, [refreshStatus]);

  // Disconnect from OAuth provider
  const disconnect = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const oauthToken = localStorage.getItem('ms_oauth_token');

      if (oauthToken) {
        // TASK-548(c): use /auth/... (like status/data-preview) — routes via the gateway
        // /auth/{path} proxy to the real endpoint AND matches the middleware allowlist, which
        // has no /api/auth catch-all. The account-less ms_oauth_token would 401 on /api/auth/...
        const response = await fetch('/auth/oauth/disconnect', {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${oauthToken}`
          }
        });

        if (!response.ok) {
          throw new Error(`Disconnect failed: ${response.statusText}`);
        }
      }

      // Clear local state — only the OAuth token, never the app session.
      localStorage.removeItem('ms_oauth_token');
      setIsAuthenticated(false);
      setUser(null);
      setStatus({ connected: false });

    } catch (error) {
      const oauthError = mapOAuthError(error);
      setError(oauthError.message);
      setErrorDetails(oauthError);
      throw new Error(oauthError.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize hook - check status on mount
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  return {
    // State
    isAuthenticated,
    user,
    status,
    isLoading,
    error,
    errorDetails,

    // Actions
    connect,
    disconnect,
    refreshStatus,
    clearError,

    // PKCE Flow helpers
    isConnecting
  };
};

export default useOAuth;